import {getTestProps} from '@benchling/js-test-utils';
import {naturalSort} from '@benchling/js-utils';
import gql from 'graphql-tag';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {User} from 'constants/UserConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Entry} from 'notebook/ELNConstants';
import {
  EntryReviewersQuery,
  EntryReviewersQueryVariables,
} from 'notebook/entries/__generated/EntryReviewersQuery';
import EntryExportConfig, {INCREMENTAL_EXPORT_DISCLOSURE} from 'notebook/entries/EntryExportConfig';
import TitleEditor from 'notebook/notes/core-ui/TitleEditor';
import {isReviewHistoryItem, Review, ReviewAction, ReviewHistoryItem} from 'notebook/reviews/ReviewConstants';
import {
  shouldShowNewReviewHistory,
  actionAndStatusText,
  getStatusTextFromReview,
} from 'notebook/reviews/ReviewUtil';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import Spinner from 'ui-library/Spinner';
import APIUtil from 'util/APIUtil';
import {formalTimeUTC} from 'util/DateUtil';
import {typeToName} from 'util/NameUtil';
import uniqueId from 'util/uniqueId';
import {StageEntryDocument} from 'workflows/WorkflowConstants';

import ReviewHistoryContainer from './ReviewHistoryContainer';

export interface EntryExportMetadataProps {
  entry: Entry | StageEntryDocument;
  exportConfig: EntryExportConfig;
  reviewChangeDate?: moment.Moment;
}

export interface EntryExportMetadataWithEsignaturesProps extends EntryExportMetadataProps {
  reviewHistoryItems: Array<ReviewHistoryItem>;
}

export const ENTRY_REVIEWERS_QUERY = gql`
  query EntryReviewersQuery($entryId: StrictString!) @skipAudit {
    entry(id: $entryId) {
      ... on EntryNode {
        id
        review {
          ... on ReviewNode {
            id
            reviewers {
              ... on ReviewerNode {
                id
                isUserValidReviewer
                user {
                  ... on UserNode {
                    id
                    name
                    email
                  }
                }
                isTeamReassignable
              }
            }
            reviewLifecycleVersion {
              id
              terminalState
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getLegacyAuditorsToRender(review?: Review): JSX.Element | null {
  return review != null ? (
    <div>
      <b>{pluralize(review.auditors.length, 'Auditor', 'Auditors')}:</b>{' '}
      {naturalSort(review.auditors.map((u: User) => u.name || u.email)).join(', ')}
    </div>
  ) : null;
}

function getProjectName(entry: Entry | StageEntryDocument): string | null {
  let project: string | null = null;
  if ((entry as Entry).folder) {
    project = (entry as Entry).folder.source!.name;
  }
  if ((entry as StageEntryDocument).source) {
    project = (entry as StageEntryDocument).source.name;
  }
  return project;
}

export function Metadata({
  entry,
  exportConfig: {showBranding, exportedOn, isIncrementalExport},
  reviewChangeDate,
}: EntryExportMetadataProps): JSX.Element | null {
  // If we wanted to include reviewers in the entry.review, we'd need to expand Review.to_dict just for this
  // use case. Instead, we fetch it using GQL.
  // TODO(BNCH-51564): Query directly on `reviewable` rather than skipping for stage entries.
  const result = useSkippableBenchlingQuery<EntryReviewersQuery, EntryReviewersQueryVariables>(
    ENTRY_REVIEWERS_QUERY,
    {
      variables: {entryId: entry.id},
      skip: APIUtil.isStageEntry(entry.id),
    }
  );

  // TODO(T10340) pass in licenses properly so we can check hasEnterprisePlan() instead of entry.review?

  let status = getStatusTextFromReview(entry.review?.status);
  let auditorsOrReviewers;
  if (result.type === QueryResultType.PENDING) {
    return (
      <div className="loading-spinner-background u-small-padding" {...loadingIndicatorAttrs()}>
        <div className="loading-text">loading entry metadata...</div>
        <Spinner />
      </div>
    );
  } else if (result.type === QueryResultType.ERROR) {
    // If there's an error, instead of exiting, just don't render the reviewer names.
    auditorsOrReviewers = null;
  } else if (result.type === QueryResultType.SUCCESS) {
    // Query succeeded or was skipped.
    assert(
      !isInaccessible(result.data?.entry),
      "User shouldn't have been able to export an entry if it's not accessible"
    );
    const rlcReviewers = result.data?.entry.review?.reviewers;
    if ((rlcReviewers ?? []).length > 0) {
      const validReviewers = rlcReviewers!.filter((reviewer) => reviewer.isUserValidReviewer);
      const reviewLifecycleReviewerNames = Array.from(
        // TODO(BNCH-48243): `user` should not be nullable, reviewers don't currently support null users,
        // even though the data model allows it.
        // Use a set to dedupe reviewers as same user can be assigned for multiple stages
        new Set(validReviewers.map(({user}) => user!.name || user!.email))
      );
      auditorsOrReviewers = (
        <div>
          <b>{pluralize(reviewLifecycleReviewerNames.length, 'Reviewer', 'Reviewers')}:</b>{' '}
          {naturalSort(reviewLifecycleReviewerNames).join(', ')}
        </div>
      );
      status = getStatusTextFromReview(
        entry.review?.status,
        result.data?.entry.review?.reviewLifecycleVersion?.terminalState
      );
    } else {
      auditorsOrReviewers = getLegacyAuditorsToRender(entry.review);
    }
  } else if (result.type === QueryResultType.SKIPPED) {
    auditorsOrReviewers = getLegacyAuditorsToRender(entry.review);
  }

  const projectName = getProjectName(entry);
  return (
    <div>
      {showBranding ? (
        <div>
          <i className="faded">Made with Benchling</i>
        </div>
      ) : null}
      {projectName ? (
        <div>
          <b>Project:</b> {projectName}
        </div>
      ) : null}
      <div>
        <b>{pluralize(entry.authors.length, 'Author', 'Authors')}:</b>{' '}
        {naturalSort(entry.authors.map((u: User) => (u.name || u.email).trim())).join(', ')}
      </div>
      {auditorsOrReviewers}
      {status ? (
        <div>
          <b>Status:</b> {status}
        </div>
      ) : null}
      <div>
        <b>Entry Created On:</b> {formalTimeUTC(entry.created_at)}
      </div>
      <div>
        <b>Entry Last Modified:</b> {formalTimeUTC(entry.modified_at)}
      </div>
      {reviewChangeDate ? (
        <div>
          <b>Review Action Date:</b> {formalTimeUTC(reviewChangeDate)}
        </div>
      ) : null}
      {exportedOn ? (
        <div>
          <b>Export Generated On:</b> {formalTimeUTC(exportedOn)}
        </div>
      ) : null}
      {isIncrementalExport ? (
        <div>
          <i>{INCREMENTAL_EXPORT_DISCLOSURE}</i>
        </div>
      ) : null}
    </div>
  );
}

function EntryExportMetadata({
  entry,
  exportConfig,
  reviewChangeDate,
}: EntryExportMetadataProps): JSX.Element | null {
  const {showReviewHistory, showReviewCommentHistory} = exportConfig;

  const reviewHistoryItems: Array<ReviewHistoryItem> =
    entry.review?.history?.filter((item): item is ReviewHistoryItem =>
      isReviewHistoryItem(item, reviewChangeDate)
    ) ?? [];

  const showNewReviewHistory = shouldShowNewReviewHistory(reviewHistoryItems);

  if (showNewReviewHistory) {
    return (
      <div {...getTestProps({component: 'EntryExportMetadata', element: 'root'})}>
        <EntryExportMetadataWithEsignatures
          entry={entry}
          exportConfig={exportConfig}
          reviewHistoryItems={reviewHistoryItems}
          reviewChangeDate={reviewChangeDate}
        />
      </div>
    );
  }

  return (
    <div {...getTestProps({component: 'EntryExportMetadata', element: 'root'})}>
      <Metadata entry={entry} exportConfig={exportConfig} reviewChangeDate={reviewChangeDate} />
      {showReviewHistory ? (
        <ReviewHistory
          items={reviewHistoryItems}
          showReviewLifecycleColumns={reviewHistoryItems.some((item) => item.reviewLifecycleInfo != null)}
          showReviewCommentHistory={Boolean(showReviewCommentHistory)}
          reviewableId={entry.id}
        />
      ) : null}
    </div>
  );
}

function EntryExportMetadataWithEsignatures({
  entry,
  exportConfig,
  reviewHistoryItems,
  reviewChangeDate,
}: EntryExportMetadataWithEsignaturesProps): JSX.Element | null {
  const {showReviewHistory, showReviewCommentHistory} = exportConfig;

  if (!showReviewHistory) {
    return null;
  }

  return (
    <>
      <div
        className="printable-page-container review-history-page"
        {...getTestProps({component: 'EntryExportMetadata', element: 'reviewHistoryPage'})}
      >
        <Metadata entry={entry} exportConfig={exportConfig} reviewChangeDate={reviewChangeDate} />
        <ReviewHistoryContainer
          reviewHistoryItems={reviewHistoryItems}
          showReviewCommentHistory={Boolean(showReviewCommentHistory)}
          reviewableId={entry.id}
        />
      </div>
      <div className="printable-page-container">
        <div className="visible-print">
          <TitleEditor
            title={entry.name}
            humanId={entry.humanId ?? ''}
            locked={true}
            updateTitle={undefined}
            moveHandler={() => {}}
            storeState={() => {}}
            restoreState={() => false}
            requestSelection={() => {}}
          />
        </div>
      </div>
    </>
  );
}

/**
 * A table that displays actions taken regarding the review of an entry.
 *
 * If blocking snapshots are enabled at the time of this review, then snapshot history will be displayed,
 * but otherwise it will not.
 *
 * @param showReviewLifecycleColumns - When set to true, we separate out the action and status into completely
 *  separate columns: 'Action' and 'New Entry Status', respectively, along with including a new 'Review Stage'
 *  column.
 *
 *  When set to false (for legacy reviews), the 'Action' column represents either the action taken by the
 *  user OR the status of the entry after some action was performed. It is generally explicit enough that
 *  you don't need both (as it's more verbose), but only for legacy reviews.
 *
 *  This parameter should be set to true if an entry was ever sent for review using review lifecycles,
 *  even if its most recent review is using the legacy flow.
 *  @link https://benchling.slab.com/posts/review-snapshots-exports-135vsvo4 See post for examples.
 */
export function ReviewHistory({
  items,
  showReviewLifecycleColumns,
  showReviewCommentHistory,
  reviewableId,
}: {
  items: Array<ReviewHistoryItem>;
  showReviewLifecycleColumns: boolean;
  showReviewCommentHistory: boolean;
  reviewableId: string;
}): JSX.Element {
  if (!showReviewCommentHistory) {
    // if not showing comment history, filter out the items that contain only comments
    items = items.filter((item) => item.info.reviewAction !== ReviewAction.COMMENT);
  }
  if (!items.length) {
    return <></>;
  }

  const reviewableNoun = typeToName(APIUtil.apiIdToType(reviewableId));
  const columnLabels = [
    'User',
    'Timestamp',
    'Action',
    ...(showReviewLifecycleColumns ? ['New Entry Status', 'Review Stage'] : []),
    ...(showReviewCommentHistory ? ['Comment'] : []),
  ];

  return (
    <SimpleTableEditable
      {...standaloneProps}
      cid={uniqueId('entry-export-metadata')}
      locked={true}
      table={{
        name: 'Review History',
        columnLabels,
        columnConstraints: Array(columnLabels.length).fill(null),
        widths: [150, 200, ...(showReviewLifecycleColumns ? [150, 150] : []), 150, 200],
        rowLabels: [...Array(items.length).keys()].map((x) => `${x + 1}`),
        lockedDims: true,
        table: items.map((item, index) => {
          const {action, newEntryStatus} = actionAndStatusText(
            items,
            index,
            showReviewLifecycleColumns,
            true,
            reviewableNoun
          );
          const userName = item.info.signatureData?.userFullName ?? item.user.name;
          const userEmail = item.info.signatureData?.userEmail ?? item.user.email;

          if (showReviewLifecycleColumns) {
            assert(
              newEntryStatus != null,
              'New Entry Status should be generated when showing review lifecycle columns.'
            );
          }
          return [
            {text: userName || userEmail},
            {text: formalTimeUTC(item.time)},
            {text: action},
            ...(showReviewLifecycleColumns
              ? [{text: newEntryStatus!}, {text: item.reviewLifecycleInfo?.reviewStageInfo?.name ?? ''}]
              : []),
            ...(showReviewCommentHistory ? [{text: item.info.message ?? ''}] : []),
          ];
        }),
      }}
      viewer={true}
    />
  );
}

export default EntryExportMetadata;
